import React, { forwardRef } from "react";
import "./OneClickSection.css";
import {
  FaShareAlt,      // SMO
  FaVideo,         // Video Editing
  FaSearch,        // SEO
  FaBullhorn,      // Google Ads (PPC)
  FaLaptopCode,    // Web Dev
  FaArrowRight,
  FaUserSecret
} from "react-icons/fa";
import { MdOutlineAutoGraph, MdInsights } from "react-icons/md";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

const TELEGRAM_URL = "https://t.me/darioharmon";
const openTelegram = (e) => {
  e?.preventDefault?.();
  const w = window.open(TELEGRAM_URL, "_blank", "noopener,noreferrer");
  if (!w) window.location.href = TELEGRAM_URL;
};

const container = { hidden: {}, show: { transition: { staggerChildren: 0.1, delayChildren: 0.05 } } };
const item = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: .45, ease: "easeOut" } } };

const OneClickSection = forwardRef((_, ref) => {
  const navigate = useNavigate();

  const cards = [
    {
      k: "ppc",
      title: "Google Ads (PPC) That Scales",
      desc: "Search & Performance Max with GA4/GTM, weekly creative tests & ROAS guardrails.",
      pill: "Paid Growth",
      icon: <FaBullhorn />,
      kpi: { v: "+32%", t: "Avg. CTR Uplift" },
      span: "wide",
      cta: { label: "Launch Campaigns", onClick: openTelegram }
    },
    {
      k: "seo",
      title: "Technical + Content Wins",
      desc: "90%+ technical baseline, topical clusters, schema, and internal links—white-hat.",
      pill: "SEO",
      icon: <FaSearch />,
      bigIco: <MdOutlineAutoGraph />,
      span: "tall"
    },
    {
      k: "smo",
      title: "Monthly Social Calendar",
      desc: "12–20 posts, captions, comments & optional boosting across FB/IG/YT/LinkedIn.",
      pill: "SMO",
      icon: <FaShareAlt />,
      span: "mini"
    },
    {
      k: "web",
      title: "Fast, Conversion-First Websites",
      desc: "React/WordPress, Core Web Vitals, landing page kits & clean IA.",
      pill: "Web Dev",
      icon: <FaLaptopCode />,
      progress: 88,
      span: "wide",
      cta: { label: "View Web Packages", onClick: () => navigate("/services/website-development"), ghost: true }
    },
    {
      k: "video",
      title: "Shorts, Ads & Explainers",
      desc: "Hooks in 3s, on-brand captions, exports for 9:16 • 1:1 • 16:9—ad-ready.",
      pill: "Video Editing",
      icon: <FaVideo />,
      bigIco: <MdInsights />,
      span: "tall"
    },
    {
      k: "opt",
      title: "Live Performance Tuning",
      desc: "Creative splits, bid strategies, audience lift & weekly reporting across PPC/SEO/SMO/Video.",
      badge: "Policy-safe • GA4 ready",
      span: "mini"
    },
    {
      k: "hack",
      title: "Security Testing & Defense",
      desc: "Pentests, OWASP checks, dependency risks & compliance-ready reporting.",
      pill: "Ethical Hacking",
      icon: <FaUserSecret />,
      span: "tall",
      cta: { label: "Explore Security", onClick: () => navigate("/services/ethical-hacking"), ghost: true }
    }
  ];

  return (
    <section className="uxk" ref={ref} aria-label="Services overview">
      <div className="uxk__bg" aria-hidden="true" />
      <div className="wrap uxk__wrap">
        {/* Sticky Rail */}
        <aside className="uxk__rail">
          <span className="uxk__eyebrow">Launch • Optimize • Secure</span>
          <h2 className="uxk__title">A single partner for growth, ops, and security.</h2>
          <p className="uxk__sub">
            From profitable PPC and durable SEO to modern web builds and security
            hardening—built to ship on time and scale safely.
          </p>

          <div className="uxk__actions">
            <button className="btn btn--primary" onClick={openTelegram}>
              Book a Free Demo <FaArrowRight />
            </button>
            <button className="btn btn--ghost" onClick={() => navigate("/services")}>
              Explore Services
            </button>
          </div>

          <ul className="uxk__points" role="list">
            <li><b>99.9%</b><span>On-time SLA</span></li>
            <li><b>500+</b><span>Projects delivered</span></li>
            <li><b>GA4</b><span>Clean tracking</span></li>
          </ul>
        </aside>

        {/* Mosaic Grid */}
        <motion.div
          className="uxk__grid"
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.25 }}
        >
          {cards.map((c) => (
            <motion.article key={c.k} className={`uxkCard uxkCard--${c.span}`} variants={item}>
              {/* Ribbon & Icon */}
              <header className="uxkCard__head">
                {c.pill && <span className="uxkCard__pill">{c.icon}{c.pill}</span>}
                {c.icon && !c.pill && <span className="uxkCard__ico">{c.icon}</span>}
                <span className="uxkCard__halo" aria-hidden />
              </header>

              {/* Title/desc */}
              <h3 className="uxkCard__title">{c.title}</h3>
              <p className="uxkCard__desc">{c.desc}</p>

              {/* Optional progress */}
              {typeof c.progress === "number" && (
                <div className="uxkCard__meter" aria-hidden="true">
                  <span style={{ width: `${c.progress}%` }} />
                </div>
              )}

              {/* KPI / Badge */}
              <div className="uxkCard__foot">
                {c.kpi && (
                  <div className="kpi">
                    <span className="kpi__v">{c.kpi.v}</span>
                    <span className="kpi__t">{c.kpi.t}</span>
                  </div>
                )}
                {c.badge && <span className="uxkCard__badge">{c.badge}</span>}
                {c.bigIco && <span className="uxkCard__ghost">{c.bigIco}</span>}

                {/* CTA */}
                {c.cta && (
                  <button
                    className={`btn ${c.cta.ghost ? "btn--ghost" : "btn--primary"}`}
                    onClick={c.cta.onClick}
                  >
                    {c.cta.label} <FaArrowRight />
                  </button>
                )}
              </div>
            </motion.article>
          ))}
        </motion.div>
      </div>
    </section>
  );
});

export default OneClickSection;
